import { useApiToast } from 'components/Toaster';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { User } from 'services/auth/auth.types';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';
import { useGetStatisticsDataMutation } from 'services/statistic/statistic.endpoint';
import { ENV } from 'utils/env';
import { getJwtToken } from 'features/auth';
import { StatisticsData } from 'services/statistic/statistic.types';

export const useStatisticsVM = () => {
    const currentYear = new Date().getFullYear();
    const availableYears = Array.from(new Set([currentYear, 2025])).sort((a, b) => b - a);

    const [users, setUsers] = useState<Array<User>>([]);
    const [statisticsData, setStatisticsData] = useState<StatisticsData>();
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [filter, setFilter] = useState<{
        startDate: string;
        endDate: string;
        appvs: { label: string; value: string }[];
        department: { label: string; value: string } | null;
    }>({ startDate: '', endDate: '', appvs: [], department: null });
    const jwt = useSelector(getJwtToken);

    const [getStatisticsData, { isLoading: isGetStatisticsDataLoading, isError: isGetStatisticsDataError, isSuccess: isGetStatisticsDataSuccess, error: getStatisticsDataError, data: getStatisticsDataData }] = useGetStatisticsDataMutation();

    const { showErrorToast } = useApiToast();

    const [
        getAllUsers,
        {
            isLoading: isGetAllUsersLoading,
            isError: isGetAllUsersError,
            isSuccess: isGetAllUsersSuccess,
            error: getAllUsersError,
            data: getAllUsersData
        }
    ] = useGetAllUsersMutation();

    useEffect(() => {
        if (isGetAllUsersLoading) return;
        if (isGetAllUsersError && getAllUsersError) {
            showErrorToast(getAllUsersError, {
                message: 'Erreur lors de la récupération des utilisateurs'
            });
            return;
        }
        if (isGetAllUsersSuccess && getAllUsersData) {
            setUsers(getAllUsersData.data);
            return;
        }
    }, [isGetAllUsersLoading, isGetAllUsersError, isGetAllUsersSuccess, getAllUsersError, getAllUsersData]);

    useEffect(() => {
        if (isGetStatisticsDataLoading) return;
        if (isGetStatisticsDataError && getStatisticsDataError) {
            showErrorToast(getStatisticsDataError, {
                message: 'Erreur lors de la récupération des données statistiques'
            });
        }
        if (isGetStatisticsDataSuccess && getStatisticsDataData) {
            setStatisticsData(getStatisticsDataData.data);
        }
    }, [isGetStatisticsDataLoading, isGetStatisticsDataError, isGetStatisticsDataSuccess, getStatisticsDataError, getStatisticsDataData]);

    useEffect(() => {
        getAllUsers(undefined);
    }, []);

    useEffect(() => {
        getStatisticsData({ year: selectedYear });
    }, [selectedYear, getStatisticsData]);

    const extractData = () => {
        const usersIds = filter.appvs.map((user) => user.value).join(',');
        const departmentParam = filter.department ? `&department=${filter.department.value}` : '';
        window.open(`${ENV.API_URL}/app/stats/export?start=${filter.startDate}&end=${filter.endDate}&users=${usersIds}${departmentParam}&jwt=${jwt}`, '_blank');
    };

    const exportAnnualStats = () => {
        window.open(`${ENV.API_URL}/app/stats/annual/export?year=${selectedYear}&jwt=${jwt}`, '_blank');
    };

    const exportAllData = () => {
        window.open(`${ENV.API_URL}/app/stats/export-all?jwt=${jwt}`, '_blank');
    };

    const exportDataDetail = (type: 'active' | 'outgoing' | 'first-contacts' | 'appv') => {
        window.open(`${ENV.API_URL}/app/stats/data/export?type=${type}&year=${selectedYear}&jwt=${jwt}`, '_blank');
    };

    const exportStatusCSV = () => {
        if (!statisticsData?.nbPersonsPerStatus) return;
        const csvContent = [
            ['Statut', 'Nombre'],
            ...statisticsData.nbPersonsPerStatus.map(item => [item.status, item.count.toString()])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `statistiques_par_statut_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportAppvCSV = () => {
        if (!statisticsData?.nbPersonsPerResponsiblePerson) return;
        const csvContent = [
            ['APPV', 'Nombre de dossiers'],
            ...statisticsData.nbPersonsPerResponsiblePerson.map(item => [item.fullname, item.count.toString()])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `statistiques_par_appv_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportDepartmentCSV = () => {
        if (!statisticsData?.nbPersonsPerDepartment) return;
        const csvContent = [
            ['Département', 'Nombre de dossiers actifs'],
            ...statisticsData.nbPersonsPerDepartment.map(item => [item.department, item.count.toString()])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `statistiques_par_departement_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportFirstContactsPerDepartmentCSV = () => {
        if (!statisticsData?.nbFirstContactsPerDepartment?.length) return;
        const rows = [...statisticsData.nbFirstContactsPerDepartment]
            .sort((a, b) => String(a.department).localeCompare(String(b.department)));
        const total = rows.reduce((sum, item) => sum + item.count, 0);
        const csvContent = [
            ['Département', 'Nombre de premiers contacts'],
            ...rows.map(item => [item.department, item.count.toString()]),
            ['TOTAL', total.toString()]
        ].map(row => row.join(';')).join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `premiers_contacts_par_departement_${selectedYear}.csv`;
        link.click();
    };

    const exportFirstMeetingsPerDepartmentCSV = () => {
        if (!statisticsData?.nbFirstMeetingsPerDepartment?.length) return;
        const rows = [...statisticsData.nbFirstMeetingsPerDepartment]
            .sort((a, b) => String(a.department).localeCompare(String(b.department)));
        const total = rows.reduce((sum, item) => sum + item.count, 0);
        const csvContent = [
            ['Département', 'Nombre de premières rencontres APPV'],
            ...rows.map(item => [item.department, item.count.toString()]),
            ['TOTAL', total.toString()]
        ].map(row => row.join(';')).join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `premieres_rencontres_appv_par_departement_${selectedYear}.csv`;
        link.click();
    };

    const exportPartnersCSV = () => {
        if (!statisticsData?.partnersExportRows?.length) return;

        const csvContent = [
            ['Nom du partenaire', 'Catégorie', 'Date de prise de contact', 'Date de rencontre', 'Département'],
            ...statisticsData.partnersExportRows.map((item) => [
                item.partnerName,
                item.category,
                item.contactedAt,
                item.metAt,
                item.department
            ])
        ].map((row) => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `statistiques_partenaires_${selectedYear}.csv`;
        link.click();
    };

    return {
        filter, 
        setFilter,
        users,
        extractData,
        statisticsData,
        isLoading: isGetStatisticsDataLoading,
        selectedYear,
        setSelectedYear,
        availableYears,
        exportAnnualStats,
        exportAllData,
        exportDataDetail,
        exportStatusCSV,
        exportAppvCSV,
        exportDepartmentCSV,
        exportFirstContactsPerDepartmentCSV,
        exportFirstMeetingsPerDepartmentCSV,
        exportPartnersCSV
    };
};
